import React, { useState, useRef, forwardRef, useMemo, useEffect } from 'react';
import { createStore, findNode, focusEditor, moveNodes, isExpanded, collapseSelection, useEditorRef, createNodesWithHOC, findNodePath, createPluginFactory, getNodeEntries, isBlock, getRange, select, getStartPoint, unhangRange, removeNodes } from '@udecode/plate-core';
import { createStyles } from '@udecode/plate-styled-components';
import _styled, { css } from 'styled-components';
import useMergedRef from '@react-hook/merged-ref';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import { Path } from 'slate';
import { useReadOnly } from 'slate-react';
import raf from 'raf';

const getDraggableStyles = props => createStyles({
  prefixClassNames: 'Draggable',
  ...props
}, {
  root: [{
    "position": "relative"
  }, props.isDragging && {
    "opacity": "0.5"
  }, props.selected && {
    "backgroundColor": "rgb(181, 215, 255)"
  }, css([":hover .slate-Draggable-gutterLeft{", "}"], {
    "opacity": "1"
  })],
  block: {
    "overflow": "auto"
  },
  gutterLeft: [{
    "position": "absolute",
    "top": "0px",
    "display": "flex",
    "height": "100%",
    "opacity": "0",
    "pointerEvents": "none",
    "cursor": "text"
  }, css(["transform:translateX(-100%);"])],
  blockToolbarWrapper: {
    "display": "flex",
    "height": "1.5em"
  },
  blockToolbar: {
    "display": "flex",
    "alignItems": "center",
    "marginRight": "0.25rem",
    "pointerEvents": "auto"
  },
  dragHandle: [{
    "padding": "0px",
    "backgroundColor": "rgba(0, 0, 0, 0)",
    "backgroundRepeat": "no-repeat",
    "cursor": "pointer",
    "overflow": "hidden",
    "outline": "2px solid transparent",
    "outlineOffset": "2px",
    "borderStyle": "none"
  }, {
    "minWidth": "18px",
    "height": "18px"
  }],
  dropLine: [{
    "position": "absolute",
    "left": "0px",
    "right": "0px",
    "height": "0.125rem",
    "opacity": "1"
  }, {
    "background": "#B4D5FF"
  }, props.direction === 'top' && {
    "top": "-1px"
  }, props.direction === 'bottom' && {
    "bottom": "-1px"
  }]
});

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

const dndStore = createStore('dnd')({
  isDragging: false
});

/**
 * `useDrag` hook to drag a node from the editor. `item` with `id` is required.
 *
 * On drag start:
 * - set `editor.isDragging` to true
 * - add `dragging` class to `body`
 *
 * On drag end:
 * - set `editor.isDragging` to false
 * - remove `dragging` class to `body`
 *
 * Collect:
 * - isDragging: true if mouse is dragging the block
 */
const useDragNode = (editor, {
  id,
  item,
  ...options
}) => {
  return useDrag(() => ({
    item(monitor) {
      dndStore.set.isDragging(true);
      editor.isDragging = true;
      document.body.classList.add('dragging');

      const _item = typeof item === 'function' ? item(monitor) : item;

      return {
        id,
        ..._item
      };
    },

    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    end: () => {
      dndStore.set.isDragging(false);
      editor.isDragging = false;
      document.body.classList.remove('dragging');
    },
    ...options
  }), []);
};

/**
 * If dragging a node A over another node B:
 * get the direction of node A relative to node B.
 */
const getHoverDirection = ({
  dragItem,
  id,
  monitor,
  nodeRef
}) => {
  var _nodeRef$current;

  if (!nodeRef.current) return;
  const dragId = dragItem.id; // Don't replace items with themselves

  if (dragId === id) return; // Determine rectangle on screen

  const hoverBoundingRect = (_nodeRef$current = nodeRef.current) === null || _nodeRef$current === void 0 ? void 0 : _nodeRef$current.getBoundingClientRect(); // Get vertical middle

  const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2; // Determine mouse position

  const clientOffset = monitor.getClientOffset();
  if (!clientOffset) return; // Get pixels to the top

  const hoverClientY = clientOffset.y - hoverBoundingRect.top; // Only perform the move when the mouse has crossed half of the items height
  // When dragging downwards, only move when the cursor is below 50%
  // When dragging upwards, only move when the cursor is above 50%
  // Dragging downwards
  // if (dragId < hoverId && hoverClientY < hoverMiddleY) {

  if (hoverClientY < hoverMiddleY) {
    return 'top';
  } // Dragging upwards
  // if (dragId > hoverId && hoverClientY > hoverMiddleY) {


  if (hoverClientY >= hoverMiddleY) {
    return 'bottom';
  }
};

/**
 * Get new direction if updated
 */
const getNewDirection = (previousDir, dir) => {
  if (!dir && previousDir) {
    return '';
  }

  if (dir === 'top' && previousDir !== 'top') {
    return 'top';
  }

  if (dir === 'bottom' && previousDir !== 'bottom') {
    return 'bottom';
  }
};

/**
 * Callback called on drag an drop a node with id.
 */

const onDropNode = (editor, {
  dragItem,
  monitor,
  nodeRef,
  id
}) => {
  const direction = getHoverDirection({
    dragItem,
    monitor,
    nodeRef,
    id
  });
  if (!direction) return;
  const dragEntry = findNode(editor, {
    at: [],
    match: {
      id: dragItem.id
    }
  });
  if (!dragEntry) return;
  const [, dragPath] = dragEntry;
  focusEditor(editor);
  let dropPath;

  if (direction === 'bottom') {
    var _findNode;

    dropPath = (_findNode = findNode(editor, {
      at: [],
      match: {
        id
      }
    })) === null || _findNode === void 0 ? void 0 : _findNode[1];
    if (!dropPath) return;
    if (Path.equals(dragPath, Path.next(dropPath))) return;
  }

  if (direction === 'top') {
    var _findNode2;

    const nodePath = (_findNode2 = findNode(editor, {
      at: [],
      match: {
        id
      }
    })) === null || _findNode2 === void 0 ? void 0 : _findNode2[1];
    if (!nodePath) return;
    dropPath = [...nodePath.slice(0, -1), nodePath[nodePath.length - 1] - 1];
    if (Path.equals(dragPath, dropPath)) return;
  }

  if (direction) {
    const _dropPath = dropPath;
    const before = Path.isBefore(dragPath, _dropPath) && Path.isSibling(dragPath, _dropPath);
    const to = before ? _dropPath : Path.next(_dropPath);
    moveNodes(editor, {
      at: dragPath,
      to
    });
  }
};

/**
 * Callback called when dragging a node and hovering nodes.
 */

const onHoverNode = (editor, {
  dragItem,
  monitor,
  nodeRef,
  onChangeDropLine,
  dropLine,
  id
}) => {
  const direction = getHoverDirection({
    dragItem,
    monitor,
    nodeRef,
    id
  });
  const dropLineDir = getNewDirection(dropLine, direction);
  if (dropLineDir) onChangeDropLine(dropLineDir);

  if (direction && isExpanded(editor.selection)) {
    focusEditor(editor);
    collapseSelection(editor);
  }
};

/**
 * `useDrop` hook to drop a node on the editor.
 *
 * On drop:
 * - get hover direction (top, bottom or undefined), return early if undefined
 * - dragPath: find node with id = dragItem.id, return early if not found
 * - focus editor
 * - dropPath: find node with id = id, its path should be next (bottom) or previous (top)
 * - move node from dragPath to dropPath
 *
 * On hover:
 * - get drop line direction
 * - if differs from dropLine, setDropLine is called
 *
 * Collect:
 * - isOver: true if mouse is over the block
 */
const useDropNode = (editor, {
  nodeRef,
  id,
  dropLine,
  onChangeDropLine,
  ...options
}) => {
  return useDrop({
    drop: (dragItem, monitor) => {
      onDropNode(editor, {
        nodeRef,
        id,
        dragItem,
        monitor
      });
    },
    collect: monitor => ({
      isOver: monitor.isOver()
    }),

    hover(item, monitor) {
      onHoverNode(editor, {
        nodeRef,
        id,
        dropLine,
        onChangeDropLine,
        dragItem: item,
        monitor
      });
    },

    ...options
  });
};

/**
 * {@link useDragNode} and {@link useDropNode} hooks to drag and drop a node from the editor.
 * A default preview is used to show the node being dragged, which can be customized or removed.
 * Returns the drag ref and drop line direction.
 */
const useDndNode = ({
  id,
  type,
  nodeRef,
  preview: previewOptions = {},
  drag: dragOptions,
  drop: dropOptions
}) => {
  const editor = useEditorRef();
  const [dropLine, setDropLine] = useState('');
  const [{
    isDragging
  }, dragRef, preview] = useDragNode(editor, {
    id,
    type,
    ...dragOptions
  });
  const [{
    isOver
  }, drop] = useDropNode(editor, {
    accept: type,
    id,
    nodeRef,
    dropLine,
    onChangeDropLine: setDropLine,
    ...dropOptions
  });

  if (previewOptions.disable) {
    drop(nodeRef);
    preview(getEmptyImage(), {
      captureDraggingState: true
    });
  } else if (previewOptions.ref) {
    drop(nodeRef);
    preview(previewOptions.ref);
  } else {
    preview(drop(nodeRef));
  }

  if (!isOver && dropLine) {
    setDropLine('');
  }

  return {
    isDragging,
    isOver,
    dropLine,
    dragRef
  };
};

const DRAG_ITEM_BLOCK = 'block';
/**
 * {@link useDragNode}
 */

const useDragBlock = (editor, id) => useDragNode(editor, {
  id,
  type: DRAG_ITEM_BLOCK
});

/**
 * {@link useDndNode}
 */

const useDndBlock = options => useDndNode({
  type: DRAG_ITEM_BLOCK,
  ...options
});

const DefaultDragHandle = ({
  styles,
  ...props
}) => /*#__PURE__*/React.createElement(_StyledButton, _extends({
  type: "button"
}, props, {
  $_css: styles
}));

const Draggable = props => {
  var _styles$gutterLeft, _styles$blockAndGutte, _styles$blockAndGutte2, _styles$gutterLeft$cs, _styles$gutterLeft2, _styles$blockToolbarW, _styles$blockToolbarW2, _styles$blockToolbar, _styles$blockToolbar2, _styles$dragHandle, _styles$dragHandle2, _styles$blockAndGutte3, _styles$blockAndGutte4, _styles$block$css, _styles$block, _styles$dropLine, _styles$dropLine2;

  const {
    children,
    element,
    componentRef,
    onRenderDragHandle
  } = props;
  const DragHandle = onRenderDragHandle !== null && onRenderDragHandle !== void 0 ? onRenderDragHandle : DefaultDragHandle;
  const blockRef = useRef(null);
  const rootRef = useRef(null);
  const dragWrapperRef = useRef(null);
  const multiRootRef = useMergedRef(componentRef, rootRef);
  const {
    dropLine,
    dragRef,
    isDragging
  } = useDndBlock({
    id: element.id,
    nodeRef: rootRef
  });
  const multiDragRef = useMergedRef(dragRef, dragWrapperRef);
  const styles = getDraggableStyles({ ...props,
    direction: dropLine,
    isDragging
  });
  return /*#__PURE__*/React.createElement(_StyledDiv, {
    className: styles.root.className,
    ref: multiRootRef,
    $_css2: styles.root.css
  }, /*#__PURE__*/React.createElement(_StyledDiv2, {
    className: (_styles$gutterLeft = styles.gutterLeft) === null || _styles$gutterLeft === void 0 ? void 0 : _styles$gutterLeft.className,
    contentEditable: false,
    $_css3: [...((_styles$blockAndGutte = (_styles$blockAndGutte2 = styles.blockAndGutter) === null || _styles$blockAndGutte2 === void 0 ? void 0 : _styles$blockAndGutte2.css) !== null && _styles$blockAndGutte !== void 0 ? _styles$blockAndGutte : []), ...((_styles$gutterLeft$cs = (_styles$gutterLeft2 = styles.gutterLeft) === null || _styles$gutterLeft2 === void 0 ? void 0 : _styles$gutterLeft2.css) !== null && _styles$gutterLeft$cs !== void 0 ? _styles$gutterLeft$cs : [])]
  }, /*#__PURE__*/React.createElement(_StyledDiv3, {
    className: (_styles$blockToolbarW = styles.blockToolbarWrapper) === null || _styles$blockToolbarW === void 0 ? void 0 : _styles$blockToolbarW.className,
    $_css4: (_styles$blockToolbarW2 = styles.blockToolbarWrapper) === null || _styles$blockToolbarW2 === void 0 ? void 0 : _styles$blockToolbarW2.css
  }, /*#__PURE__*/React.createElement(_StyledDiv4, {
    className: (_styles$blockToolbar = styles.blockToolbar) === null || _styles$blockToolbar === void 0 ? void 0 : _styles$blockToolbar.className,
    ref: multiDragRef,
    $_css5: (_styles$blockToolbar2 = styles.blockToolbar) === null || _styles$blockToolbar2 === void 0 ? void 0 : _styles$blockToolbar2.css
  }, /*#__PURE__*/React.createElement(DragHandle, {
    element: element,
    styles: (_styles$dragHandle = styles.dragHandle) === null || _styles$dragHandle === void 0 ? void 0 : _styles$dragHandle.css,
    className: (_styles$dragHandle2 = styles.dragHandle) === null || _styles$dragHandle2 === void 0 ? void 0 : _styles$dragHandle2.className,
    onMouseDown: e => e.stopPropagation()
  })))), /*#__PURE__*/React.createElement(_StyledDiv5, {
    ref: blockRef,
    $_css6: [...((_styles$blockAndGutte3 = (_styles$blockAndGutte4 = styles.blockAndGutter) === null || _styles$blockAndGutte4 === void 0 ? void 0 : _styles$blockAndGutte4.css) !== null && _styles$blockAndGutte3 !== void 0 ? _styles$blockAndGutte3 : []), ...((_styles$block$css = (_styles$block = styles.block) === null || _styles$block === void 0 ? void 0 : _styles$block.css) !== null && _styles$block$css !== void 0 ? _styles$block$css : [])]
  }, children, !!dropLine && /*#__PURE__*/React.createElement(_StyledDiv6, {
    className: (_styles$dropLine = styles.dropLine) === null || _styles$dropLine === void 0 ? void 0 : _styles$dropLine.className,
    contentEditable: false,
    $_css7: (_styles$dropLine2 = styles.dropLine) === null || _styles$dropLine2 === void 0 ? void 0 : _styles$dropLine2.css
  })));
};

var _StyledButton = _styled("button").withConfig({
  displayName: "Draggable___StyledButton",
  componentId: "sc-fk3pif-0"
})(["", ""], p => p.$_css);

var _StyledDiv = _styled("div").withConfig({
  displayName: "Draggable___StyledDiv",
  componentId: "sc-fk3pif-1"
})(["", ""], p => p.$_css2);

var _StyledDiv2 = _styled("div").withConfig({
  displayName: "Draggable___StyledDiv2",
  componentId: "sc-fk3pif-2"
})(["", ""], p => p.$_css3);

var _StyledDiv3 = _styled("div").withConfig({
  displayName: "Draggable___StyledDiv3",
  componentId: "sc-fk3pif-3"
})(["", ""], p => p.$_css4);

var _StyledDiv4 = _styled("div").withConfig({
  displayName: "Draggable___StyledDiv4",
  componentId: "sc-fk3pif-4"
})(["", ""], p => p.$_css5);

var _StyledDiv5 = _styled("div").withConfig({
  displayName: "Draggable___StyledDiv5",
  componentId: "sc-fk3pif-5"
})(["", ""], p => p.$_css6);

var _StyledDiv6 = _styled("div").withConfig({
  displayName: "Draggable___StyledDiv6",
  componentId: "sc-fk3pif-6"
})(["", ""], p => p.$_css7);

const GrabberTooltipContent = () => /*#__PURE__*/React.createElement("div", {
  style: {
    fontSize: 12
  }
}, /*#__PURE__*/React.createElement("div", null, "Drag ", /*#__PURE__*/React.createElement("span", {
  style: {
    color: 'rgba(255, 255, 255, 0.45)'
  }
}, "to move")));

const grabberTooltipProps = {
  content: /*#__PURE__*/React.createElement(GrabberTooltipContent, null),
  placement: 'bottom',
  arrow: false,
  offset: [0, 0],
  delay: [300, 0],
  duration: [0, 0],
  hideOnClick: true,
  theme: 'small'
};

const withDraggable = (Component, {
  styles,
  level = 0,
  filter,
  allowReadOnly = false,
  onRenderDragHandle
} = {}) => {
  return /*#__PURE__*/forwardRef((props, ref) => {
    const {
      attributes,
      element,
      editor
    } = props;
    const readOnly = useReadOnly();
    const path = useMemo(() => findNodePath(editor, element), [editor, element]);
    const filteredOut = useMemo(() => path && (Number.isInteger(level) && level !== path.length - 1 || filter && filter(editor, path)), [path, editor]);

    if (filteredOut || !allowReadOnly && readOnly) {
      return /*#__PURE__*/React.createElement(Component, props);
    }

    return /*#__PURE__*/React.createElement(Draggable, {
      editor: editor,
      attributes: attributes,
      element: element,
      componentRef: ref,
      styles: styles,
      onRenderDragHandle: onRenderDragHandle
    }, /*#__PURE__*/React.createElement(Component, props));
  });
};
const withDraggables = createNodesWithHOC(withDraggable);

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now = function() {
  return _root.Date.now();
};

var now_1 = now;

/** Used to match a single whitespace character. */
var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim(string) {
  return string
    ? string.slice(0, _trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim;

/** Built-in value references. */
var Symbol = _root.Symbol;

var _Symbol = Symbol;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto$1.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol_1(value)) {
    return NAN;
  }
  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = _baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber;

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  wait = toNumber_1(wait) || 0;
  if (isObject_1(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber_1(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now_1();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now_1());
  }

  function debounced() {
    var time = now_1(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a throttled function that only invokes `func` at most once per
 * every `wait` milliseconds. The throttled function comes with a `cancel`
 * method to cancel delayed `func` invocations and a `flush` method to
 * immediately invoke them. Provide `options` to indicate whether `func`
 * should be invoked on the leading and/or trailing edge of the `wait`
 * timeout. The `func` is invoked with the last arguments provided to the
 * throttled function. Subsequent calls to the throttled function return the
 * result of the last `func` invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the throttled function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.throttle` and `_.debounce`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to throttle.
 * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=true]
 *  Specify invoking on the leading edge of the timeout.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new throttled function.
 * @example
 *
 * // Avoid excessively updating the position while scrolling.
 * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
 *
 * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
 * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
 * jQuery(element).on('click', throttled);
 *
 * // Cancel the trailing throttled invocation.
 * jQuery(window).on('popstate', throttled.cancel);
 */
function throttle(func, wait, options) {
  var leading = true,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  if (isObject_1(options)) {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce_1(func, wait, {
    'leading': leading,
    'maxWait': wait,
    'trailing': trailing
  });
}

var throttle_1 = throttle;

const getCoords = e => {
  if (e.type === 'touchmove') {
    return {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };
  }

  return {
    x: e.clientX,
    y: e.clientY
  };
};

const ScrollArea = ({
  placement,
  enabled = true,
  height = 100,
  zIndex = 10000,
  minStrength = 0.15,
  strengthMultiplier = 25,
  containerRef,
  scrollAreaProps
}) => {
  const ref = useRef();
  const scaleYRef = useRef(0);
  const frameRef = useRef(null);
  const direction = placement === 'top' ? -1 : 1; // Drag a fixed, invisible box of custom height at the top, and bottom
  // of the window. Make sure to show it only when dragging something.

  const style = {
    position: 'fixed',
    height,
    width: '100%',
    opacity: 0,
    zIndex,
    ...(scrollAreaProps === null || scrollAreaProps === void 0 ? void 0 : scrollAreaProps.style)
  };

  if (placement === 'top') {
    style.top = 0;
  } else if (placement === 'bottom') {
    style.bottom = 0;
  }

  const stopScrolling = () => {
    scaleYRef.current = 0;

    if (frameRef.current) {
      raf.cancel(frameRef.current);
      frameRef.current = null;
    }
  };

  const startScrolling = () => {
    const tick = () => {
      var _containerRef$current;

      const scaleY = scaleYRef.current; // stop scrolling if there's nothing to do

      if (strengthMultiplier === 0 || scaleY === 0) {
        stopScrolling();
        return;
      }

      const container = (_containerRef$current = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) !== null && _containerRef$current !== void 0 ? _containerRef$current : window;
      container.scrollBy(0, scaleY * strengthMultiplier * direction);
      frameRef.current = raf(tick); // there's a bug in safari where it seems like we can't get
      // mousemove events from a container that also emits a scroll
      // event that same frame. So we should double the strengthMultiplier and only adjust
      // the scroll position at 30fps
    };

    tick();
  }; // Update scaleY every 100ms or so
  // and start scrolling if necessary


  const updateScrolling = throttle_1(e => {
    const container = ref.current;
    if (!container) return;
    const {
      top: y,
      height: h
    } = container.getBoundingClientRect();
    const coords = getCoords(e);
    const strength = Math.max(Math.max(coords.y - y, 0) / h, minStrength); // calculate strength

    scaleYRef.current = direction === -1 ? 1 - strength : strength; // start scrolling if we need to

    if (!frameRef.current && scaleYRef.current) {
      startScrolling();
    }
  }, 100, {
    trailing: false
  });

  const handleEvent = e => {
    updateScrolling(e);
  };

  useEffect(() => {
    if (!enabled) {
      stopScrolling();
    }
  }, [enabled]);
  if (!enabled) return null; // Hide the element if not enabled, so it doesn't interfere with clicking things under it.

  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    style: style,
    onDragOver: handleEvent,
    onDragLeave: stopScrolling,
    onDragEnd: stopScrolling // touchmove events don't seem to work across siblings, so we unfortunately
    // would have to attach the listeners to the body
    ,
    onTouchMove: handleEvent
  }, scrollAreaProps));
};

/**
 * Set up an edge scroller at the top of the page for scrolling up.
 * One at the bottom for scrolling down.
 */
const Scroller = props => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ScrollArea, _extends({
    placement: "top"
  }, props)), /*#__PURE__*/React.createElement(ScrollArea, _extends({
    placement: "bottom"
  }, props)));
};

const DndScroller = props => {
  const isDragging = dndStore.use.isDragging();
  return /*#__PURE__*/React.createElement(Scroller, _extends({
    enabled: isDragging
  }, props));
};

const KEY_DND = 'dnd';
const createDndPlugin = createPluginFactory({
  key: KEY_DND,
  handlers: {
    onDragStart: () => () => dndStore.set.isDragging(true),
    onDragEnd: () => () => dndStore.set.isDragging(false),
    onDrop: editor => () => editor.isDragging
  },
  then: (editor, {
    options
  }) => ({
    renderAfterEditable: options.enableScroller ? () => /*#__PURE__*/React.createElement(DndScroller, options === null || options === void 0 ? void 0 : options.scrollerProps) : undefined
  })
});

/**
 * {@link useDropNode}
 */

const useDropBlock = (editor, options) => useDropNode(editor, {
  accept: DRAG_ITEM_BLOCK,
  ...options
});

/**
 * Get blocks with an id
 */

const getBlocksWithId = (editor, options) => {
  const _nodes = getNodeEntries(editor, {
    match: n => isBlock(editor, n) && !!n.id,
    ...options
  });

  return Array.from(_nodes);
};

/**
 * Get node entries range.
 */

const getNodesRange = (editor, nodeEntries) => {
  if (!nodeEntries.length) return;
  const firstBlockPath = nodeEntries[0][1];
  const lastBlockPath = nodeEntries[nodeEntries.length - 1][1];
  return getRange(editor, firstBlockPath, lastBlockPath);
};

/**
 * Select the start of a block by id and focus the editor.
 */

const focusBlockStartById = (editor, id) => {
  var _findNode;

  const path = (_findNode = findNode(editor, {
    at: [],
    match: {
      id
    }
  })) === null || _findNode === void 0 ? void 0 : _findNode[1];
  if (!path) return;
  select(editor, getStartPoint(editor, path));
  focusEditor(editor);
};

/**
 * Remove blocks with an id and focus the editor.
 */

const removeBlocksAndFocus = (editor, options) => {
  unhangRange(editor, options === null || options === void 0 ? void 0 : options.at, options);
  const nodeEntries = getBlocksWithId(editor, options);
  removeNodes(editor, {
    at: getNodesRange(editor, nodeEntries)
  });
  focusEditor(editor);
};

/**
 * Select the block above the selection by id and focus the editor.
 */

const selectBlockById = (editor, id) => {
  var _findNode;

  const path = (_findNode = findNode(editor, {
    at: [],
    match: {
      id
    }
  })) === null || _findNode === void 0 ? void 0 : _findNode[1];
  if (!path) return;
  select(editor, getRange(editor, path));
  focusEditor(editor);
};

/**
 * Select blocks by selection or by id.
 * If the block with id is not selected, select the block with id.
 * Else, select the blocks above the selection.
 */

const selectBlocksBySelectionOrId = (editor, id) => {
  if (!editor.selection) return;
  const blockEntries = getBlocksWithId(editor, {
    at: editor.selection
  });
  const isBlockSelected = blockEntries.some(blockEntry => blockEntry[0].id === id);

  if (isBlockSelected) {
    select(editor, getNodesRange(editor, blockEntries));
    focusEditor(editor);
  } else {
    selectBlockById(editor, id);
  }
};

export { DRAG_ITEM_BLOCK, DndScroller, Draggable, KEY_DND, ScrollArea, Scroller, createDndPlugin, dndStore, focusBlockStartById, getBlocksWithId, getDraggableStyles, getHoverDirection, getNewDirection, getNodesRange, grabberTooltipProps, onDropNode, onHoverNode, removeBlocksAndFocus, selectBlockById, selectBlocksBySelectionOrId, useDndBlock, useDndNode, useDragBlock, useDragNode, useDropBlock, useDropNode, withDraggable, withDraggables };
//# sourceMappingURL=index.es.js.map
